import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { Button, Modal } from '@hotjar/hotsauce';
import type { Site } from '@hotjar/site';

import { connect } from '../../domains/slack/connect';
import type { GenericSuccessData } from '../../domains/slack/api/common';

export interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  currentSite: Site | undefined;
  onConnect: (data: GenericSuccessData) => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export const EnableSlackModal = ({
  isOpen,
  onRequestClose,
  currentSite,
  onConnect,
  onSuccess,
  onError,
}: Props): JSX.Element => {
  const [isConnecting, setIsConnecting] = useState(false);
  const { t } = useTranslation();
  const { id, name } = currentSite ?? {};

  const handleConnect = () => {
    if (id) {
      setIsConnecting(true);
      connect(id)
        .then((data) => {
          onConnect(data);
          setIsConnecting(false);

          onSuccess(t('integrations:slack.enable-modal.notify-success', { name }));
        })
        .catch((error) => {
          setIsConnecting(false);

          if (!error.message) {
            return;
          }

          onError(error.message);
        });
    }
  };

  return (
    <Modal
      contentLabel={t('integrations:slack.enable-modal.content-label')}
      isOpen={isOpen}
      onClose={onRequestClose}
    >
      <Modal.Header title={t('integrations:slack.enable-modal.title')} />
      <Modal.Body>
        <Trans i18nKey="integrations:slack.enable-modal.description">
          Slack isn&apos;t connected to {{ name }} right now. To enable it you&apos;ll be taken
          through the process in a Slack popup window.
        </Trans>
      </Modal.Body>
      <Modal.Footer>
        <Button
          data-testid="enable-slack-button"
          kind="primary"
          onClick={handleConnect}
          isDisabled={isConnecting}
        >
          {t('integrations:slack.enable-modal.action')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
